import { memo, ReactElement } from 'react'
import { Box } from '@mui/material'

export type GhostFigureVariant = 'bars' | 'seasonal' | 'search'

const W = 280
const H = 88

/** Hollow quarterly-results bars over a baseline, brightest in the middle. */
const Bars = () => {
    const heights = [30, 44, 38, 56, 50, 64, 58]
    const center = (heights.length - 1) / 2
    return (
        <>
            <line x1="8" y1="77.5" x2={W - 8} y2="77.5" stroke="currentColor" strokeWidth="1" opacity="0.25" />
            {heights.map((h, i) => {
                const x = 22 + i * 36
                const opacity = Math.max(0.14, 0.55 - Math.abs(i - center) * 0.1)
                return <rect key={i} x={x + 0.5} y={77 - h} width={19} height={h} rx="1.5" fill="none" stroke="currentColor" strokeWidth="1" opacity={opacity} />
            })}
        </>
    )
}

/** Three years of the same twelve months, drawn over a month axis. */
const seasonalPath = (amplitude: number, phase: number, offset: number) =>
    Array.from({ length: 13 }, (_, i) => {
        const x = 8 + i * 22
        const y = 42 + offset + amplitude * Math.sin((i / 12) * Math.PI * 2 + phase)
        return `${x},${y.toFixed(1)}`
    }).join(' ')

const Seasonal = () => (
    <>
        {Array.from({ length: 13 }, (_, i) => (
            <line key={i} x1={8 + i * 22} y1="76" x2={8 + i * 22} y2="80" stroke="currentColor" strokeWidth="1" opacity="0.25" />
        ))}
        <line x1="8" y1="78.5" x2={W - 8} y2="78.5" stroke="currentColor" strokeWidth="1" opacity="0.25" />
        <polyline points={seasonalPath(20, 2.6, -4)} fill="none" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" opacity="0.18" />
        <polyline points={seasonalPath(12, 1.4, 6)} fill="none" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" opacity="0.32" />
        <polyline points={seasonalPath(16, 0, 0)} fill="none" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" opacity="0.5" />
    </>
)

/** A search field with a term typed in, and the result rows underneath fading to nothing. */
const Search = () => (
    <>
        <rect x="40.5" y="8.5" width="199" height="26" rx="6" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.45" />
        <circle cx="56" cy="21" r="4.5" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.45" />
        <line x1="59.5" y1="24.5" x2="63" y2="28" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.45" />
        <rect x="72" y="18" width="46" height="6" rx="3" fill="currentColor" opacity="0.4" />
        <rect x="122" y="18" width="24" height="6" rx="3" fill="currentColor" opacity="0.4" />
        <line x1="150.5" y1="15" x2="150.5" y2="27" stroke="currentColor" strokeWidth="1" opacity="0.45" />
        {[0.28, 0.16, 0.08].map((opacity, i) => {
            const y = 50 + i * 15
            return (
                <g key={i} opacity={opacity} fill="currentColor">
                    <circle cx="50" cy={y} r="4" />
                    <rect x="62" y={y - 3} width={[64, 48, 56][i]} height="6" rx="3" />
                    <rect x="196" y={y - 3} width={[36, 30, 40][i]} height="6" rx="3" />
                </g>
            )
        })}
    </>
)

const FIGURES: Record<GhostFigureVariant, () => ReactElement> = {
    bars: Bars,
    seasonal: Seasonal,
    search: Search
}

type Props = {
    variant: GhostFigureVariant
}

/** Quiet outline of the chart a page would show if it had data. Sibling of GhostRows for non-list surfaces. */
const GhostFigure = ({ variant }: Props) => (
    <Box aria-hidden sx={{ display: 'flex', justifyContent: 'center', color: 'text.secondary' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
            {FIGURES[variant]()}
        </svg>
    </Box>
)

export default memo(GhostFigure)
